import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { InfoWithTooltip, TextField } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import {
    UpdateUserDetailsDocument,
    UserDetailsInput,
} from '@graphql/generated/graphql';

import { useUserBasic } from '@utils/hooks/useUserBasic';

import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { alreadyFilledOutPersonalInfoVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    emailValidation,
    firstNameValidation,
    lastNameValidation,
} from '@shared/validations';

import { TooltipWrapper } from './PersonalInfoForm.styled';

interface PersonalInfoFormProps {
    callback?: () => void;
    disclosureUuid: string;
    isOrganization?: boolean;
    setIsDisabled?: (value: boolean) => void;
    setIsLoading?: (value: boolean) => void;
}

export const PERSONAL_INFO_FORM_ID = 'PERSONAL_INFO_FORM';

export const PersonalInfoForm = ({
    callback,
    disclosureUuid,
    isOrganization,
    setIsDisabled,
    setIsLoading,
}: PersonalInfoFormProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data, loading: dataLoading } = useUserBasic();

    const [updateUserDetails, { loading }] = useMutation(
        UpdateUserDetailsDocument,
        {
            context: {
                notifyOnError: true,
            },
            onError: () => {},
        },
    );

    const {
        formState: { errors, isSubmitted, isValid },
        handleSubmit,
        register,
        reset,
    } = useForm<ChangePersonalInfoFormValues>({
        mode: 'onTouched',
        resolver: yupResolver(getChangePersonalInfoFormSchema(isOrganization)),
    });

    const onSubmit = handleSubmit(
        async ({
            firstName,
            lastName,
            organizationEmail,
            organizationName,
            role,
        }) => {
            alreadyFilledOutPersonalInfoVar(true);

            const userDetailsInput: UserDetailsInput = {
                country: data?.user.country,
                firstName: firstName,
                lastName: lastName,
                organizationRole: role,
                ...(isOrganization && {
                    organizationEmail,
                    organizationName,
                }),
            };

            await updateUserDetails({
                onCompleted: () => {
                    navigate(
                        getPath({
                            params: {
                                disclosureUuid,
                            },
                            path: '/disclosure/details/:disclosureUuid',
                        }),
                    );
                    reset();
                    callback?.();
                },
                variables: {
                    userDetailsInput,
                },
            });
        },
    );

    useEffect(() => {
        if (!dataLoading && data?.user) {
            const {
                firstName,
                lastName,
                organizationEmail,
                organizationName,
                organizationRole,
            } = data.user;

            reset({
                firstName: firstName || '',
                lastName: lastName || '',
                organizationEmail,
                organizationName,
                role: organizationRole || '',
            });
        }
    }, [data, dataLoading]);

    useEffect(() => {
        setIsDisabled?.(!isValid && isSubmitted);
    }, [isValid, isSubmitted]);

    useEffect(() => {
        setIsLoading?.(loading);
    }, [loading]);

    return (
        <StandardForm id={PERSONAL_INFO_FORM_ID} onSubmit={onSubmit}>
            {isOrganization && (
                <>
                    <TextField
                        {...register('organizationName')}
                        className="htmlToImageIgnore"
                        error={!!errors.organizationName}
                        label={t('ORGANISATION_NAME_LABEL')}
                        message={t(errors.organizationName?.message || '')}
                        placeholder={t('ORGANISATION_NAME_PLACEHOLDER')}
                        testId="UIMHqfl"
                    />
                    <TextField
                        {...register('organizationEmail')}
                        className="htmlToImageIgnore"
                        error={!!errors.organizationEmail}
                        label={
                            <TooltipWrapper>
                                <span>{t('ORGANISATION_EMAIL_LABEL')}</span>
                                <InfoWithTooltip
                                    testId="tE-Vydh"
                                    tooltipOptions={{
                                        text: t('ORGANISATION_EMAIL_TOOLTIP'),
                                        width: 324,
                                    }}
                                />
                            </TooltipWrapper>
                        }
                        message={t(errors.organizationEmail?.message || '')}
                        placeholder={t('ORGANISATION_EMAIL_PLACEHOLDER')}
                        testId="z6_NXcc"
                        type="email"
                    />
                </>
            )}

            <TextField
                {...register('firstName')}
                className="htmlToImageIgnore"
                error={!!errors.firstName}
                label={t('FIRST_NAME')}
                message={t(errors.firstName?.message || '')}
                placeholder={t('FIRST_NAME_PLACEHOLDER')}
                testId="QNPSrBs"
            />
            <TextField
                {...register('lastName')}
                className="htmlToImageIgnore"
                error={!!errors.lastName}
                label={t('LAST_NAME')}
                message={t(errors.lastName?.message || '')}
                placeholder={t('LAST_NAME_PLACEHOLDER')}
                testId="tV1rflg"
            />
            <TextField
                {...register('role')}
                error={!!errors.role}
                label={t('ROLE')}
                message={t(errors.role?.message || '')}
                placeholder={t('ROLE_PLACEHOLDER')}
                testId="wMVUlrA"
            />
        </StandardForm>
    );
};

const getChangePersonalInfoFormSchema = (isOrganization?: boolean) =>
    yup.object({
        firstName: firstNameValidation,
        lastName: lastNameValidation,
        organizationEmail: isOrganization
            ? emailValidation
            : yup.string().email().optional().nullable(),
        organizationName: isOrganization
            ? yup.string().required('FIELD_CANNOT_BE_EMPTY')
            : yup.string().optional().nullable(),
        role: yup.string().required('FIELD_CANNOT_BE_EMPTY'),
    });

export type ChangePersonalInfoFormValues = yup.InferType<
    ReturnType<typeof getChangePersonalInfoFormSchema>
>;
